// src/app/governance/advisory-board/page.tsx - Industry Advisory Board with tree structure
'use client';

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Skeleton,
  Collapse,
  IconButton,
  Link as MuiLink,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  Email,
  Link as LinkIcon,
  Work,
  Business,
  AccountTree,
  Person,
} from '@mui/icons-material';
import Image from 'next/image';
import Link from 'next/link';

// Import API functions
import { api, getMediaUrl } from '@/lib/api';

// Import our modular color system
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';
import PageHero from '@/components/PageHero';

// Person interface - matching your Strapi structure
interface PersonData {
  id: number;
  name: string;
  title: string;
  email?: string;
  bio?: string;
  personType: string;
  linkedProfile?: string;
  specialRole?: string;
  affiliation?: {
    name: string;
    website?: string;
    organizationType?: string;
  };
  photo?: {
    url: string;
    formats?: {
      thumbnail?: { url: string };
      small?: { url: string };
      medium?: { url: string };
    };
  } | null;
}

// =====================
// STYLED COMPONENTS
// =====================

const TreeContainer = styled(Box)(({ theme }) => {
  const colors = theme.colors;
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '48px',
    padding: '32px 0',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '240px', // Start below the chair card
      left: '50%',
      transform: 'translateX(-50%)',
      width: '2px',
      height: 'calc(100% - 480px)', // Adjust height to account for chair card
      background: `linear-gradient(180deg, ${colors.brand.accent.main} 0%, ${withOpacity(colors.brand.accent.main, 0.3)} 100%)`,
      zIndex: 1,
    }
  };
});

const ChairSection = styled(Box)(({ theme }) => {
  const colors = theme.colors;
  return {
    position: 'relative',
    zIndex: 3, // Higher z-index to be above tree line
    width: '100%',
    maxWidth: '600px',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-24px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0',
      height: '0',
      borderLeft: '12px solid transparent',
      borderRight: '12px solid transparent',
      borderTop: `12px solid ${colors.brand.accent.main}`,
      zIndex: 4,
    }
  };
});

const MembersGrid = styled(Box)(({ theme }) => {
  const colors = theme.colors;
  return {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-24px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: colors.brand.accent.main,
      border: `4px solid ${colors.background.default}`,
    }
  };
});

const AdvisoryCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isChair',
})<{ isChair?: boolean }>(({ theme, isChair }) => {
  const colors = theme.colors;
  return {
    background: isChair 
      ? colors.background.paper // Solid background for chair to hide tree line
      : colors.gradients.card,
    border: isChair 
      ? `2px solid ${colors.brand.accent.main}`
      : `1px solid ${colors.border.primary}`,
    borderRadius: '20px',
    marginBottom: '24px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'visible', // Allow the CHAIR badge to extend outside
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-4px)',
      boxShadow: isChair
        ? `0 12px 40px ${withOpacity(colors.brand.accent.main, 0.3)}`
        : `0 8px 25px ${colors.effects.glow}`,
    },
    
    ...(isChair && {
      '&::before': {
        content: '"CHAIR"',
        position: 'absolute',
        top: '-12px',
        right: '16px',
        background: colors.gradients.primary,
        color: colors.brand.accent.contrastText,
        padding: '6px 20px',
        borderRadius: '16px',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
        zIndex: 10,
        boxShadow: `0 2px 8px ${withOpacity(colors.brand.accent.main, 0.4)}`,
      }
    })
  };
});

const AdvisoryAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'isChair',
})<{ isChair?: boolean }>(({ theme, isChair }) => {
  const colors = theme.colors;
  return {
    width: isChair ? 120 : 100,
    height: isChair ? 120 : 100,
    border: isChair 
      ? `4px solid ${colors.brand.accent.main}`
      : `3px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
    fontSize: isChair ? '2.5rem' : '2rem',
    fontWeight: 600,
    background: isChair 
      ? colors.gradients.primary
      : withOpacity(colors.brand.accent.main, 0.1),
    color: isChair 
      ? colors.brand.accent.contrastText
      : colors.brand.accent.main,
    
    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'scale(1.05)',
    },
  };
});

const RoleChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isChair',
})<{ isChair?: boolean }>(({ theme, isChair }) => {
  const colors = theme.colors;
  return {
    background: isChair 
      ? colors.gradients.primary
      : withOpacity(colors.brand.accent.main, 0.15),
    color: isChair 
      ? colors.brand.accent.contrastText
      : colors.brand.accent.main,
    border: `1px solid ${colors.brand.accent.main}`,
    fontSize: '0.75rem',
    fontWeight: 600,
    height: '28px',
  };
});

const OrganizationChip = styled(Chip)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: withOpacity(colors.text.secondary, 0.1),
    color: colors.text.secondary,
    border: `1px solid ${withOpacity(colors.text.secondary, 0.3)}`,
    fontSize: '0.7rem',
    height: '24px',
    '&:hover': {
      background: withOpacity(colors.text.secondary, 0.2),
    },
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface AdvisoryMemberProps {
  member: PersonData;
  isChair?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

const AdvisoryMember = React.memo<AdvisoryMemberProps>(({ 
  member, 
  isChair = false, 
  isExpanded, 
  onToggle 
}) => {
  const colors = useColorTheme();
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getOrganizationType = (type?: string) => {
    switch (type?.toLowerCase()) {
      case 'government': return { icon: <Business />, color: '#4caf50' };
      case 'industry': return { icon: <Work />, color: '#2196f3' };
      case 'university': return { icon: <AccountTree />, color: '#ff9800' };
      default: return { icon: <Business />, color: colors.text.secondary };
    }
  };

  const orgType = getOrganizationType(member.affiliation?.organizationType);

  return (
    <AdvisoryCard isChair={isChair} onClick={onToggle}>
      <CardContent sx={{ p: isChair ? 5 : 4 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          {/* Avatar */}
          <AdvisoryAvatar isChair={isChair}>
            {member.photo ? (
              <Image
                src={getMediaUrl(member.photo)}
                alt={member.name}
                unoptimized={true}
                width={isChair ? 120 : 100}
                height={isChair ? 120 : 100}
                style={{ objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              getInitials(member.name)
            )}
          </AdvisoryAvatar>

          {/* Basic Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <RoleChip
                icon={isChair ? <AccountTree /> : <Person />}
                label={isChair ? 'Advisory Board Chair' : 'Advisory Board Member'}
                size="small"
                isChair={isChair}
              />
              {member.affiliation?.organizationType && (
                <OrganizationChip
                  icon={orgType.icon}
                  label={member.affiliation.organizationType}
                  size="small"
                  sx={{ color: orgType.color, borderColor: orgType.color }}
                />
              )}
            </Box>

            <Typography
              variant={isChair ? "h4" : "h5"}
              sx={{
                color: colors.brand.accent.dark,
                fontWeight: 700,
                marginBottom: 1,
                lineHeight: 1.2,
                fontSize: isChair ? { xs: '1.8rem', md: '2.2rem' } : { xs: '1.3rem', md: '1.5rem' },
              }}
            >
              {member.name}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: colors.text.secondary,
                marginBottom: 1,
                fontWeight: 500,
                fontSize: isChair ? '1rem' : '0.9rem',
              }}
            >
              {member.title}
            </Typography>

            {member.affiliation && (
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.disabled,
                  display: 'block',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                {member.affiliation.name}
              </Typography>
            )}
            <Typography
                variant="body1"
                sx={{
                  color: colors.primary.main,
                  display: 'block',
                  pt: 2,
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                Click to view bio
              </Typography>
          </Box>

          {/* Expand/Collapse Button */}
          <IconButton
            size="small"
            sx={{
              color: colors.brand.accent.dark,
              backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
              '&:hover': {
                background: withOpacity(colors.brand.accent.main, 0.2),
                transform: 'scale(1.1)',
              }
            }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Expandable Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ 
            borderColor: withOpacity(colors.brand.accent.main, 0.2), 
            mb: 3,
            mx: -2,
          }} />

          {/* Bio */}
          {member.bio && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Person sx={{ fontSize: '1.3rem' }} />
                Biography
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.secondary,
                  lineHeight: 1.7,
                  fontSize: '1rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {member.bio}
              </Typography>
            </Box>
          )}

          {/* Contact & Links */}
          <Box sx={{ 
            pt: 3, 
            borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center'
          }}>
            {member.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                <MuiLink
                  href={`mailto:${member.email}`}
                  sx={{
                    color: colors.brand.accent.dark,
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {member.email}
                </MuiLink>
              </Box>
            )}

            {member.linkedProfile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                <MuiLink
                  href={member.linkedProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: colors.brand.accent.dark,
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Professional Profile
                </MuiLink>
              </Box>
            )}

            {member.affiliation?.website && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Work sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                <MuiLink
                  href={member.affiliation.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: colors.brand.accent.dark,
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {member.affiliation.name}
                </MuiLink>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </AdvisoryCard>
  );
});

AdvisoryMember.displayName = 'AdvisoryMember';

const LoadingSkeleton = React.memo(() => {
  const colors = useColorTheme();
  
  return (
    <TreeContainer>
      {/* Chair Skeleton */}
      <ChairSection>
        <Card sx={{ 
          background: colors.gradients.card, 
          border: `2px solid ${colors.border.primary}`,
          borderRadius: '20px',
        }}>
          <CardContent sx={{ p: 5 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Skeleton 
                variant="circular" 
                width={120} 
                height={120} 
                sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
              />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton 
                  variant="rectangular" 
                  height={28} 
                  width="50%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 2 }} 
                />
                <Skeleton 
                  variant="text" 
                  height={32} 
                  width="80%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 1 }} 
                />
                <Skeleton 
                  variant="text" 
                  height={24} 
                  width="70%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 1 }} 
                />
                <Skeleton 
                  variant="text" 
                  height={20} 
                  width="60%" 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </ChairSection>

      {/* Members Skeleton */}
      <MembersGrid>
        <Grid container spacing={3}>
          {Array.from({ length: 4 }, (_, index) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              <Card sx={{ 
                background: colors.gradients.card, 
                border: `1px solid ${colors.border.primary}`,
                borderRadius: '20px',
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                    <Skeleton 
                      variant="circular" 
                      width={100} 
                      height={100} 
                      sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton 
                        variant="rectangular" 
                        height={24} 
                        width="40%" 
                        sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 2 }} 
                      />
                      <Skeleton 
                        variant="text" 
                        height={28} 
                        width="80%" 
                        sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 1 }} 
                      />
                      <Skeleton 
                        variant="text" 
                        height={20} 
                        width="70%" 
                        sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MembersGrid>
    </TreeContainer>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const AdvisoryBoardPage: React.FC = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [advisoryMembers, setAdvisoryMembers] = React.useState<PersonData[]>([]);

  const colors = useColorTheme();

  React.useEffect(() => {
    const fetchAdvisoryBoard = async () => {
      try {
        setLoading(true);
        
        // Fetch people with Industry Advisory Board roles
        const peopleData = await api.getPeople();
        
        // Filter for advisory board members
        const advisoryBoardMembers = peopleData.filter(person => 
          person.personType === 'Industry Advisory Board Chair' || 
          person.personType === 'Industry Advisory Board Member'
        );
        
        console.log('Advisory Board data:', advisoryBoardMembers);
        setAdvisoryMembers(advisoryBoardMembers);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch advisory board:', err);
        setError(err instanceof Error ? err.message : 'Failed to load advisory board');
        setAdvisoryMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvisoryBoard();
  }, []);

  const handleCardToggle = useCallback((personId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personId)) {
        newSet.delete(personId);
      } else {
        newSet.add(personId);
      }
      return newSet;
    });
  }, []);

  const { chair, members } = useMemo(() => {
    const chair = advisoryMembers.find(member => 
      member.personType === 'Industry Advisory Board Chair'
    );
    const members = advisoryMembers.filter(member => 
      member.personType === 'Industry Advisory Board Member'
    );
    
    return { chair, members };
  }, [advisoryMembers]);

  const stats = useMemo(() => {
    const governmentMembers = advisoryMembers.filter(m => 
      m.affiliation?.organizationType === 'Government'
    ).length;
    const industryMembers = advisoryMembers.filter(m => 
      m.affiliation?.organizationType === 'Industry'
    ).length;
    const totalMembers = advisoryMembers.length;

    return { governmentMembers, industryMembers, totalMembers };
  }, [advisoryMembers]);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      <PageHero
        label="Governance"
        title="Industry Advisory Board"
        subtitle="Strategic leadership from industry and government partners guiding our cybersecurity research priorities"
        icon={<Business />}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ 
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 3,
            textAlign: 'center',
            minWidth: '140px',
            borderRadius: 2,
          }}>
            <AccountTree sx={{ color: colors.brand.accent.dark, fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700 }}>
              {stats.totalMembers}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Advisory Board Members
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
              Unable to Load Advisory Board
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {error}
            </Typography>
          </Box>
        ) : advisoryMembers.length > 0 ? (
          <TreeContainer>
            {/* Chair Section */}
            {chair && (
              <ChairSection>
                <AdvisoryMember
                  member={chair}
                  isChair={true}
                  isExpanded={expandedCards.has(chair.id)}
                  onToggle={() => handleCardToggle(chair.id)}
                />
              </ChairSection>
            )}

            {/* Members Section */}
            {members.length > 0 && (
              <MembersGrid>
                <Grid container spacing={3}>
                  {members.map((member) => (
                    <Grid key={member.id} size={{ xs: 12, md: 6 }}>
                      <AdvisoryMember
                        member={member}
                        isChair={false}
                        isExpanded={expandedCards.has(member.id)}
                        onToggle={() => handleCardToggle(member.id)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MembersGrid>
            )}

            {/* Mission Statement */}
            <Box sx={{ 
              mt: 6,
              p: 4,
              background: colors.background.elevated, // Solid background instead of gradient
              border: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
              borderRadius: 3,
              position: 'relative',
              zIndex: 3, // Higher z-index to be above tree line
            }}>
              <Typography
                variant="h5"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  marginBottom: 3,
                  textAlign: 'center',
                }}
              >
                Advisory Board Mission
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.secondary,
                  lineHeight: 1.7,
                  textAlign: 'center',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                }}
              >
                Our Industry Advisory Board provides strategic guidance on research priorities, 
                industry trends, and workforce development needs. Comprising leaders from government 
                and industry sectors, the board ensures our cybersecurity research remains relevant 
                to real-world challenges and opportunities in emerging technologies.
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 4,
                mt: 4,
                flexWrap: 'wrap',
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: colors.brand.accent.dark, fontWeight: 600 }}>
                    Strategic Guidance
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                    Research direction & priorities
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: colors.brand.accent.dark, fontWeight: 600 }}>
                    Industry Insights
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                    Market trends & challenges
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: colors.brand.accent.dark, fontWeight: 600 }}>
                    Workforce Development
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                    Skills & training needs
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TreeContainer>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No Advisory Board Members Found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Advisory board information will appear here once it&apos;s added to the system.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default AdvisoryBoardPage;